import {
  ChipBadge,
  IconWrapper,
  LinkButton,
  PrimaryButton,
  theme,
} from '@geneo2-web/shared-ui';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  Button,
  Slide,
  SxProps,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useState } from 'react';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { IStyles } from '../../../commonUtils/styleUtils';
import { NoContentCard } from '../NoContent.tsx';
const styles: IStyles = {
  root: {},

  heading: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(2), md: pxTovW(8) },
    padding: {
      // md: `${pxTovW(24)} ${pxTovW(18)} 0`,
    },
    paddingBottom: 0,
  },

  itemWrapper: {
    display: 'flex',
    overflowX: 'auto',
    justifyContent: 'flex-start',
    gap: { xs: pxToRem(16), md: pxTovW(18) },
    paddingTop: { xs: pxToRem(20), md: pxTovW(20) },
    paddingBottom: { xs: 0, md: pxTovW(40) },
    '&::-webkit-scrollbar': { display: 'none' },
    paddingLeft: { xs: pxToRem(22), md: pxTovW(22) },
    paddingRight: { xs: pxToRem(22), md: pxTovW(22) },
    boxSizing: 'border-box',
    '&>div': { flexShrink: 0 },
  },
  carouselButton: {
    display: { xs: 'none', md: 'inline-flex' },
    padding: 0,
    minWidth: 0,
    width: { xs: pxTovW(28), md: pxToRem(18), lg: pxTovW(28) },
    height: { xs: pxTovW(28), md: pxToRem(18), lg: pxTovW(28) },
    borderRadius: pxTovW(5),
    border: '1px solid grey',
    marginLeft: pxTovW(10),
  },
};
interface IProps {
  title: string | React.ReactNode;
  linkButtonText?: string;
  linkButtonHandler?: () => void;
  backgroundColor?: string;
  items?: React.ReactNode[];
  itemsToShow?: number;
  maxItemsToShow?: number;
  containerMdWidth: string;
  isError?: boolean;
  errorMessage?: string;
  noContentMessage?: string;
  totalNoOfItems?: number; // can be more that items.length as we  dont want to display  all items in homepage
  rootStyle?: SxProps;
}
export const SectionListWithDotCarousel = (props: IProps) => {
  const {
    title,
    linkButtonHandler,
    linkButtonText,
    backgroundColor = '#F5FAFF',
    items = [],
    maxItemsToShow = 20,
    itemsToShow = 3,
    containerMdWidth,
    isError,
    errorMessage,
    noContentMessage,
    totalNoOfItems,
    rootStyle,
  } = props;

  const dynamicStyle: IStyles = {
    root: { width: { xs: '100%', md: containerMdWidth } },
  };
  let rootSx = { ...dynamicStyle.root };
  if (rootStyle) rootSx = { ...rootSx, ...rootStyle };

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = React.useRef(null);
  const totalPages = Math.ceil(items.length / itemsToShow);

  const handleDotClick = (pageIndex: number) => {
    setCurrentIndex(pageIndex * itemsToShow);
  };
  return (
    <Box sx={rootSx}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxSizing: 'border-box',
          padding: {
            xs: `${pxToRem(20)} ${pxToRem(8)}`,
            md: `${pxTovW(18)} ${pxTovW(24)}`,
          },
        }}
      >
        <Box sx={styles.heading}>
          {typeof title === 'string' ? (
            <Typography
              variant="h2"
              fontWeight="medium"
              sx={{
                // width: { xs: pxToRem(120), md: 'inherit' },
                width: '100%',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                margin: 'auto',
              }}
            >
              {title}
            </Typography>
          ) : (
            <>{title}</>
          )}

          <ChipBadge
            label={totalNoOfItems ? totalNoOfItems : items?.length}
            color="primary"
            size="small"
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h4"
            onClick={linkButtonHandler}
            sx={{
              color: '#007CDC',
              textDecorationColor: '#007CDC',
              textDecorationLine: 'underline',
              cursor: 'pointer',
            }}
          >
            {linkButtonText}
          </Typography>
        </Box>
      </Box>
      {isError ? (
        <Box
          width={{ xs: undefined, md: '100%' }}
          marginY={{ xs: pxToRem(20), md: pxTovW(40) }}
          marginLeft={{ xs: 0, md: pxTovW(40) }}
        >
          <NoContentCard
            variant="error"
            icon="error"
            text={errorMessage || 'Error Occured'}
          />
        </Box>
      ) : items.length === 0 ? (
        <NoContentCard
          variant="info"
          icon="cards"
          text={noContentMessage || 'No Data to show'}
          rootStyle={{ marginTop: { xs: pxToRem(20), md: pxTovW(40) } }}
        />
      ) : (
        <>
          <Box
            sx={{
              ...styles.itemWrapper,
              background: {
                xs: `linear-gradient(to bottom, white 33%,${
                  backgroundColor ? backgroundColor : '#FCF1C7'
                } 33% 66%,white 66% 100%)`,
                md: `linear-gradient(to bottom, white 40%,${
                  backgroundColor ? backgroundColor : '#EAF4FC'
                } 20% 80%)`,
              },
            }}
            ref={containerRef}
          >
            {items.map((item, index) => {
              const isVisible =
                index >= currentIndex && index < currentIndex + itemsToShow;
              return (
                <Slide
                  key={index}
                  direction={isVisible ? 'left' : 'right'}
                  in={isMobile ? true : isVisible}
                  mountOnEnter
                  unmountOnExit
                  container={containerRef.current}
                >
                  <div>{item}</div>
                </Slide>
              );
            })}
            {totalNoOfItems &&
              totalNoOfItems > maxItemsToShow &&
              linkButtonHandler && (
                <Slide
                  key="see-all"
                  direction="left"
                  in={
                    isMobile
                      ? true
                      : currentIndex + itemsToShow > items.length - 1 &&
                        currentIndex <= items.length
                  }
                  mountOnEnter
                  unmountOnExit
                  container={containerRef.current}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      width: { xs: pxToRem(200), md: pxTovW(300) },
                      height: { xs: pxToRem(150), md: pxTovW(200) },
                      marginY: 'auto',
                      alignItems: 'center',
                      backgroundColor: '#F5FAFF',
                      borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
                      cursor: 'pointer',
                    }}
                    onClick={linkButtonHandler}
                  >
                    <IconWrapper
                      name="see-all"
                      type="png"
                      parentFolder="icons"
                      customSx={{
                        width: { xs: pxToRem(40), md: pxTovW(48) },
                        height: { xs: pxToRem(40), md: pxTovW(48) },
                      }}
                    />
                    <Typography variant="h4">See All</Typography>
                  </Box>
                </Slide>
              )}
          </Box>
          {/* Dot Carousel */}
          {!isMobile && items.length > 3 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: { xs: pxToRem(12), md: pxTovW(18) },
                gap: pxToRem(8),
              }}
            >
              {Array.from({ length: totalPages }).map((_, pageIndex) => (
                <Box
                  key={pageIndex}
                  onClick={() => handleDotClick(pageIndex)}
                  sx={{
                    width: pxToRem(10),
                    height: pxToRem(10),
                    borderRadius: '50%',
                    backgroundColor:
                      currentIndex / itemsToShow === pageIndex
                        ? '#F8C807'
                        : '#FEEDAE',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                />
              ))}
            </Box>
          ) : (
            <></>
          )}
        </>
      )}
    </Box>
  );
};
